import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  buildEvTestedDataPath,
  buildFuelCostsPath,
  VehicleEntities,
  VehicleModel,
} from 'client/data/models/vehicle-v2';
import { VisitorEntities } from 'client/data/models/visitor';
import { MpgEntities } from 'client/data/models/mpg';
import { MpgParams } from 'site-modules/shared/utils/mpg/params';
import { EV_TESTED_DATA_START_DATE } from 'site-modules/shared/constants/range-and-cost';
import { EDMUNDS_LOGO_NO_TEXT } from 'client/images/inline-svgs/edmunds-logo';
import { getYoutubePreviewImage, YOUTUBE_PREVIEW_SIZE } from 'client/utils/image-helpers';
import { getCustomCoreUrl } from 'site-modules/shared/utils/core-link-constructor';
import { srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';
import { getUsedRange, getUsedYears } from 'site-modules/shared/utils/core-page/ev-insights';
import { Link } from 'site-modules/shared/components/link/link';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { ShoppingLink } from 'site-modules/shared/components/shopping-link/shopping-link';
import { TreehousePromotionCheck } from 'site-modules/shared/components/treehouse-promotion/treehouse-promotion-check';
import { ReadyForEv, ReadyForEvTreehouse } from 'site-modules/shared/components/ready-for-ev/ready-for-ev';
import { InsightAd } from 'site-modules/shared/components/native-ad/insight-ad/insight-ad';
import { CostToDrive } from './cards/cost-to-drive';
import { ElectricityRange } from './cards/electricity-range';
import { TimeToCharge } from './cards/time-to-charge';
import { EpaMpg } from './cards/epa-mpg';
import { TailpipeEmissions } from './cards/tailpipe-emissions';
import { MpgCard } from './mpg-card';

import './mpg-cards.scss';

function MpgCardsUI({
  params,
  style,
  styleParams: { year, makeName, modelName, trimName },
  evTestedData = null,
  fuelCostsData = null,
  personalizationParams,
  visitorLocation = null,
  isPHEV = false,
  isElectric = false,
  isUsed = false,
  titleTag,
  isPreview = false,
  isMobile = false,
}) {
  const vehicleName = `${modelName} ${trimName}`;
  const averageCategory = `${get(fuelCostsData, 'vehicleData.vehicleSizeClass', '')} ${get(
    fuelCostsData,
    'vehicleData.primaryBodyType'
  )}`.trim();

  const evRangeHeading = isUsed ? 'Estimated Range Based on Age' : 'Range';
  const testedRange = get(evTestedData, 'range');
  const epaRange = +get(style, 'evData.range');
  const totalRange = testedRange || epaRange || 0;
  const usedYears = getUsedYears({ year, isUsed });
  const currentRange = getUsedRange({ totalRange, year, isUsed });

  const readyForEvHeading = isPHEV ? 'Am I Ready for a Plug-In Hybrid?' : 'Am I Ready for an EV?';

  const edmundsTestedBadge = (
    <div
      className="tested-badge px-0_75 py-0_25 fw-medium small text-primary-darker text-nowrap d-flex align-items-center"
      style={{ letterSpacing: '1px' }}
    >
      <EDMUNDS_LOGO_NO_TEXT width={18} fillColor="#0069bf" />
      &nbsp;edmunds&nbsp;
      <span className="fw-bold text-cool-gray-50" style={{ letterSpacing: '3px' }}>
        TESTED
      </span>
    </div>
  );

  return (
    <div
      className={classnames('mpg-cards py-1', {
        'bg-cool-gray-90 rounded-12 px-0_75 px-md-1': !isPreview,
        ev: isElectric,
        phev: isPHEV,
        gas: !isElectric && !isPHEV,
      })}
    >
      <MpgCard heading="Cost to Drive" creativeId="cost-to-drive" titleTag={titleTag}>
        <CostToDrive
          modelName={vehicleName}
          fuelCostsData={fuelCostsData}
          stateName={get(visitorLocation, 'state')}
          isPHEV={isPHEV}
          isElectric={isElectric}
        />
      </MpgCard>
      {(isElectric || isPHEV) && (
        <MpgCard
          heading={isPHEV ? 'All Electric Range' : evRangeHeading}
          creativeId="electricity-range"
          badge={testedRange ? edmundsTestedBadge : undefined}
          titleTag={titleTag}
        >
          <ElectricityRange
            vehicleName={`${year} ${makeName} ${modelName}`}
            modelName={vehicleName}
            currentRange={currentRange}
            isTestedRange={!!testedRange}
            isEstimatedRange={!!usedYears}
            visitorLocation={visitorLocation}
            isPluginHybrid={isPHEV}
            isPreview={isPreview}
          />
        </MpgCard>
      )}
      {!isElectric && (
        <MpgCard heading="Est. Combined City/Hwy" creativeId="mpg" titleTag={titleTag}>
          <EpaMpg style={style} cityPercents={personalizationParams.cityPercents} />
        </MpgCard>
      )}
      {(isElectric || isPHEV) && (
        <MpgCard heading="Charging at Home" creativeId="time-to-charge" titleTag={titleTag}>
          <TimeToCharge
            chargeTime={get(style, 'evData.timeToCharge')}
            portType={get(style, 'evData.fastChargePortType')}
            isPreview={isPreview}
          />
        </MpgCard>
      )}
      {!isPreview && (
        <Fragment>
          <MpgCard heading="Tailpipe Emissions" creativeId="tailpipe-emissions" titleTag={titleTag}>
            <TailpipeEmissions
              style={style}
              averageCategory={averageCategory}
              modelName={vehicleName}
              fuelCostsData={fuelCostsData}
              isElectric={isElectric}
              isPHEV={isPHEV}
              personalizationParams={personalizationParams}
            />
          </MpgCard>
          {(isElectric || isPHEV) && (
            <TreehousePromotionCheck
              fallback={
                <MpgCard heading={readyForEvHeading} titleTag={titleTag}>
                  <ReadyForEv isMobileLayout />
                </MpgCard>
              }
            >
              <MpgCard heading={readyForEvHeading} titleTag={titleTag}>
                <ReadyForEvTreehouse isMobileLayout isTreehousePromo creativeId="treehouse-promotion" />
              </MpgCard>
            </TreehousePromotionCheck>
          )}
          {(isElectric || isPHEV) && (
            <Link
              to="/car-buying/hybrid-vs-electric-vs-plug-in-hybrid.html"
              className="mpg-card d-flex align-items-center text-decoration-none pos-r px-1 py-1 rounded-12 bg-white mb-1"
            >
              <div style={{ width: '132px', flexShrink: 0 }}>
                <AspectRatioContainer className="preview-image-container">
                  <img src={getYoutubePreviewImage('o6lPDOf-wEs', YOUTUBE_PREVIEW_SIZE.MQ)} alt="" />
                </AspectRatioContainer>
              </div>
              <div className="medium fw-medium ms-1">
                Hybrid vs. Electric vs. Plug-In Hybrid — What&apos;s the Difference? Which Is Best for You?
              </div>
            </Link>
          )}

          <div className="mpg-card links">
            <ShoppingLink
              label={`Learn more about ${year} ${makeName} ${modelName}`}
              to={getCustomCoreUrl({ makeSlug: params.make, modelSlug: params.model, year }, isUsed ? 'review/' : '')}
              className="bg-white px-0_75 py-0_75 rounded-12 mb-1"
              iconClassName="icon-stack"
              iconContainerClassName="me-0_75 text-blue-50 size-20"
              labelClassName="fw-bold"
              iconStyles={{ height: '40px', width: '40px' }}
              data-tracking-id="view_content"
              data-tracking-value="more_about"
              data-test="more-about-mmy-link"
            />

            <ShoppingLink
              label={
                <Fragment>
                  See all{' '}
                  <span className="visually-hidden">
                    {makeName} ${modelName} vehicles
                  </span>{' '}
                  for sale
                </Fragment>
              }
              to={srpLinkBuilder({ make: params.make, model: params.model })}
              rel="nofollow"
              className="bg-white px-0_75 py-0_75 rounded-12 mb-1"
              iconClassName="icon-price-tags2"
              iconContainerClassName="me-0_75 text-blue-50 size-20"
              labelClassName="fw-bold"
              iconStyles={{ height: '40px', width: '40px' }}
              data-tracking-id="view_search_result"
            />
          </div>
          <InsightAd mobile={isMobile} wrapperClass="mpg-card mb-1" />
        </Fragment>
      )}
    </div>
  );
}

MpgCardsUI.propTypes = {
  params: MpgParams.isRequired,
  style: MpgEntities.StyleMpg.isRequired,
  styleParams: PropTypes.shape({
    year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    makeName: PropTypes.string,
    modelName: PropTypes.string,
    trimName: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  personalizationParams: PropTypes.shape({
    dailyCommute: PropTypes.number,
    milesPerYear: PropTypes.number,
    cityPercents: PropTypes.number,
  }).isRequired,
  evTestedData: VehicleEntities.StyleEvTestedData,
  fuelCostsData: VehicleEntities.StyleFuelCostData,
  visitorLocation: VisitorEntities.Location,
  isElectric: PropTypes.bool,
  isPHEV: PropTypes.bool,
  isUsed: PropTypes.bool,
  titleTag: PropTypes.string,
  isPreview: PropTypes.bool,
  isMobile: PropTypes.bool,
};

export const propsAreEqual = (prevProps, { evTestedData, fuelCostsData, params, isElectric }) =>
  !fuelCostsData || (!evTestedData && isElectric && params.year >= EV_TESTED_DATA_START_DATE);

export const MpgCards = connectToModel(React.memo(MpgCardsUI, propsAreEqual), {
  evTestedData: bindToPath(
    ({ style, params, isElectric }) =>
      isElectric && params.year >= EV_TESTED_DATA_START_DATE && buildEvTestedDataPath([style.id]),
    VehicleModel,
    evTestedData => get(evTestedData, '[0]', {})
  ),
  fuelCostsData: bindToPath(
    ({ style, personalizationParams }) => buildFuelCostsPath(style.id, personalizationParams),
    VehicleModel
  ),
});
